/**
 * Query key factories shared across hook modules.
 *
 * These live apart from the hook files so cross-cutting code (cache
 * invalidation in `userScopedQueries`, SSR hydrators) can build keys without
 * pulling each hook's endpoint modules and adapters into its bundle.
 */

import type { SearchListingsRequest } from '@/src/lib/api/endpoints/search';
import type { UserListingType } from '@/src/lib/api/endpoints/user';
import type { SupportedLocale } from '@/src/i18n/routing';

export const hpToursKeys = {
  all: ['hpTours'] as const,
  scheduledToursForUser: ['hpTours', 'scheduledToursForUser'] as const,
};

/**
 * Deliberately app-owned rather than codegen's `HpApiInquiryV3Handler_prefill` key: this cache
 * entry holds the unwrapped `HpInquiryPrefillDto`, while codegen types its own key as the
 * `HpApiResponseDto` envelope. Sharing the key would let a later generated-hook call or SSR
 * prefetch type-check against `response.data` and find it undefined at runtime.
 */
export const inquiryKeys = {
  all: ['inquiry'] as const,
  contacted: (alias: string, email: string) => [...inquiryKeys.all, 'contacted', email.toLowerCase(), alias] as const,
  prefill: () => [...inquiryKeys.all, 'prefill'] as const,
};

export const listingDetailKeys = {
  all: ['listingDetail'] as const,
  rentalContent: (listingAlias: string, locale: SupportedLocale) =>
    ['listingDetail', 'rentalContent', listingAlias, locale] as const,
  byMaloneLotId: (
    maloneLotIdEncoded: string,
    type: 'unit' | 'building',
    unit?: string | null,
    locale: SupportedLocale = 'en',
  ) => ['listingDetail', 'byMaloneLotId', maloneLotIdEncoded, type, normalizeListingDetailUnit(unit) ?? '', locale] as const,
};

export function normalizeListingDetailUnit(unit: string | null | undefined): string | null {
  const normalizedUnit = unit?.trim();
  return normalizedUnit || null;
}

export const searchKeys = {
  all: ['search'] as const,
  listings: (request: SearchListingsRequest) => ['search', 'listings', request] as const,
  // Separate namespace: these entries cache a bare unit count, not a
  // SearchListingsResult, so they must never collide with `listings`.
  unitCount: (request: SearchListingsRequest) => ['search', 'unitCount', request] as const,
};

export const filterMetadataKeys = {
  all: ['filterMetadata'] as const,
};

export const recentSearchKeys = {
  all: ['recentSearch'] as const,
  search: () => [...recentSearchKeys.all, 'search'] as const,
};

export const savedSearchKeys = {
  all: ['savedSearch'] as const,
  list: () => [...savedSearchKeys.all, 'list'] as const,
};

// Outside savedSearchKeys.all, so editing or deleting one saved search does not re-run every card's search.
export const savedSearchResultKeys = {
  byViewUrl: (viewUrl: string) => ['savedSearchResults', viewUrl] as const,
};

export const userListingKeys = {
  all: ['userListings'] as const,
  byType: (type: UserListingType) => [...userListingKeys.all, type] as const,
};
